// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationForm.jsx

import React from "react";

import {
  FaAt,
  FaBuilding,
  FaCalendarAlt,
  FaGlobe,
  FaHashtag,
  FaImage,
  FaLink,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaServer,
  FaStar,
} from "react-icons/fa";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  ASSOCIATION_PRODUCTS,
  ASSOCIATION_QUILL_FORMATS,
  ASSOCIATION_QUILL_MODULES,
  ASSOCIATION_STATUS_VALUES,
} from "../data/associations.data";

import {
  FormField,
  FormSection,
  getFormInputClass,
} from "../ui/FormField";

const fallbackLogo = "/images/no-image.jpg";

function IconField({ icon: Icon, children }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-slate-400">
        <Icon />
      </span>

      {children}
    </div>
  );
}

export default function AssociationForm({
  formData,
  errors = {},
  disabled = false,
  onChange,
  onDetailChange,
}) {
  const logoPreview =
    formData.logo_asso?.trim() || fallbackLogo;

  return (
    <div className="space-y-5">
      <FormSection
        title="Identité et suivi commercial"
        description="Informations principales et position actuelle dans le parcours commercial."
        icon={FaBuilding}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Nom du club, de l’association ou du client"
            required
            error={errors.nom_asso}
            className="sm:col-span-2"
          >
            <input
              type="text"
              name="nom_asso"
              value={formData.nom_asso}
              onChange={onChange}
              disabled={disabled}
              placeholder="Ex. Judo Club Lantonnais"
              className={getFormInputClass(
                errors.nom_asso,
              )}
            />
          </FormField>

          <FormField
            label="Produit"
            required
            error={errors.produit_asso}
          >
            <select
              name="produit_asso"
              value={formData.produit_asso}
              onChange={onChange}
              disabled={disabled}
              className={getFormInputClass(
                errors.produit_asso,
              )}
            >
              <option value="">
                Sélectionner un produit
              </option>

              {ASSOCIATION_PRODUCTS.map((product) => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Statut commercial"
            required
            error={errors.statut_asso}
          >
            <select
              name="statut_asso"
              value={formData.statut_asso}
              onChange={onChange}
              disabled={disabled}
              className={getFormInputClass(
                errors.statut_asso,
              )}
            >
              {ASSOCIATION_STATUS_VALUES.map(
                (status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ),
              )}
            </select>
          </FormField>

          <FormField
            label="Dernier contact"
            hint="Date du dernier échange"
          >
            <IconField icon={FaCalendarAlt}>
              <input
                type="date"
                name="last_contact_asso"
                value={formData.last_contact_asso}
                onChange={onChange}
                disabled={disabled}
                className={`${getFormInputClass(
                  errors.last_contact_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Prochaine relance"
            hint="Apparaîtra dans les alertes"
          >
            <IconField icon={FaCalendarAlt}>
              <input
                type="date"
                name="date_relance_asso"
                value={formData.date_relance_asso}
                onChange={onChange}
                disabled={disabled}
                className={`${getFormInputClass(
                  errors.date_relance_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <div className="sm:col-span-2">
            <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300">
                  <FaStar />
                </span>

                <div>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    Association favorite
                  </div>

                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Permet de retrouver rapidement ce dossier.
                  </div>
                </div>
              </div>

              <input
                type="checkbox"
                name="is_favorite"
                checked={formData.is_favorite}
                onChange={onChange}
                disabled={disabled}
                className="h-5 w-5 accent-amber-500"
              />
            </label>
          </div>
        </div>
      </FormSection>

      <FormSection
        title="Contact et localisation"
        description="Coordonnées utilisées pour les appels, emails et la cartographie."
        icon={FaMapMarkerAlt}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Téléphone"
            error={errors.tel_asso}
          >
            <IconField icon={FaPhoneAlt}>
              <input
                type="tel"
                name="tel_asso"
                value={formData.tel_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="06 12 34 56 78"
                className={`${getFormInputClass(
                  errors.tel_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Adresse email"
            error={errors.mail_asso}
          >
            <IconField icon={FaAt}>
              <input
                type="email"
                name="mail_asso"
                value={formData.mail_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="contact@association.fr"
                className={`${getFormInputClass(
                  errors.mail_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Adresse postale"
            error={errors.adresse_asso}
            className="sm:col-span-2"
          >
            <IconField icon={FaMapMarkerAlt}>
              <input
                type="text"
                name="adresse_asso"
                value={formData.adresse_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="71 rue du Général de Gaulle, 95320 Saint-Leu-la-Forêt"
                className={`${getFormInputClass(
                  errors.adresse_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Latitude"
            error={errors.latitude}
          >
            <input
              type="text"
              inputMode="decimal"
              name="latitude"
              value={formData.latitude}
              onChange={onChange}
              disabled={disabled}
              placeholder="44.8378"
              className={getFormInputClass(
                errors.latitude,
              )}
            />
          </FormField>

          <FormField
            label="Longitude"
            error={errors.longitude}
          >
            <input
              type="text"
              inputMode="decimal"
              name="longitude"
              value={formData.longitude}
              onChange={onChange}
              disabled={disabled}
              placeholder="-0.5792"
              className={getFormInputClass(
                errors.longitude,
              )}
            />
          </FormField>

          <FormField
            label="Numéro SIRET"
            error={errors.siret_asso}
          >
            <IconField icon={FaHashtag}>
              <input
                type="text"
                name="siret_asso"
                value={formData.siret_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="123 456 789 00012"
                className={`${getFormInputClass(
                  errors.siret_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Discipline(s)"
            error={errors.discipline_asso}
            hint="Séparées par des virgules"
          >
            <input
              type="text"
              name="discipline_asso"
              value={formData.discipline_asso}
              onChange={onChange}
              disabled={disabled}
              placeholder="Judo, Ju-Jitsu, Taïso"
              className={getFormInputClass(
                errors.discipline_asso,
              )}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        title="Sites et ressources"
        description="Adresses du site, de l’API et du logo de l’organisation."
        icon={FaLink}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Site internet"
            error={errors.siteweb_asso}
          >
            <IconField icon={FaGlobe}>
              <input
                type="url"
                name="siteweb_asso"
                value={formData.siteweb_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="https://www.exemple.fr"
                className={`${getFormInputClass(
                  errors.siteweb_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="Adresse de l’API"
            error={errors.apiUrl_asso}
          >
            <IconField icon={FaServer}>
              <input
                type="url"
                name="apiUrl_asso"
                value={formData.apiUrl_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="https://api.exemple.fr"
                className={`${getFormInputClass(
                  errors.apiUrl_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <FormField
            label="URL ou chemin du logo"
            error={errors.logo_asso}
            hint="URL complète ou /img/logo.png"
            className="sm:col-span-2"
          >
            <IconField icon={FaImage}>
              <input
                type="text"
                inputMode="url"
                name="logo_asso"
                value={formData.logo_asso}
                onChange={onChange}
                disabled={disabled}
                placeholder="https://www.exemple.fr/logo.png"
                className={`${getFormInputClass(
                  errors.logo_asso,
                )} pl-10`}
              />
            </IconField>
          </FormField>

          <div className="sm:col-span-2">
            <div className="flex min-h-28 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/40">
              <img
                src={logoPreview}
                alt="Aperçu du logo"
                className="max-h-24 max-w-full object-contain"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = fallbackLogo;
                }}
              />
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        title="Notes et historique"
        description="Informations commerciales, échanges passés et éléments à retenir."
        icon={FaGlobe}
      >
        <div className="overflow-hidden rounded-xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950/60">
          <ReactQuill
            theme="snow"
            value={formData.detail_asso}
            onChange={onDetailChange}
            modules={ASSOCIATION_QUILL_MODULES}
            formats={ASSOCIATION_QUILL_FORMATS}
            readOnly={disabled}
            placeholder="Ajoutez des notes sur l’association, les échanges, les attentes du client…"
          />
        </div>
      </FormSection>
    </div>
  );
}